/* cart-drawer.js — current behavior + rich diagnostics
   Toggle logs: window.__CART_DEBUG__ = true/false (default true)
*/
window.__CART_DEBUG__ = window.__CART_DEBUG__ ?? true;
const DBG = (...args) => { if (window.__CART_DEBUG__) console.log(...args); };

/* ----- Small utilities (idempotent) ----- */
if (!window.replaceCartIconBubbleHTML) {
  window.replaceCartIconBubbleHTML = function replaceCartIconBubbleHTML(sectionHTML) {
    try {
      const target = document.querySelector('#cart-icon-bubble');
      if (!target || !sectionHTML) return;
      const doc = new DOMParser().parseFromString(sectionHTML, 'text/html');
      const src = doc.querySelector('#cart-icon-bubble');
      if (src) target.innerHTML = src.innerHTML;
      DBG('[Cart Count] 🔁 replaceCartIconBubbleHTML');
    } catch (e) { console.warn('[Cart Count] replaceCartIconBubbleHTML failed:', e); }
  };
}

if (!window.refreshCartBadgeFromCart) {
  window.refreshCartBadgeFromCart = async function refreshCartBadgeFromCart() {
    try {
      const cart = await fetch('/cart.js', { credentials: 'same-origin' }).then(r => r.json());
      const count = cart.items.reduce((sum, item) => {
        const tags = item.product_tags || item.tags || [];
        const tagArr = Array.isArray(tags) ? tags : String(tags).split(',');
        const hasMonogram = tagArr.map(t => String(t).trim().toLowerCase()).includes('monogram fee');
        return hasMonogram ? sum : sum + (item.quantity || 0);
      }, 0);
      document.querySelectorAll('[data-cart-count]').forEach(el => el.textContent = String(count));
      document.querySelectorAll('[data-cart-count-ada]').forEach(el => el.textContent = `${count} item${count === 1 ? '' : 's'}`);
      document.querySelectorAll('[data-cart-counter-bubble]').forEach(el => el.classList.toggle('hidden', count <= 0));
      DBG('[Cart Count] ✅ Updated from cart.js →', count);
    } catch (err) { console.warn('[Cart Count] ❌ Failed to update from cart.js:', err); }
  };
}

/* ----- Popup helpers (diagnostic visibility only; no hard guard) ----- */
function __scanRebuyPopupCandidates() {
  const sel = [
    '.rebuy-widget-popup',
    '.rebuy-modal',
    '.rebuy-modal-container',
    '.rebuy__modal',
    '[data-rebuy-widget-type="popup"]',
    '[class*="rebuy"][class*="modal"]',
    '[class*="rebuy"][class*="popup"]',
    '[role="dialog"][class*="rebuy"]'
  ].join(',');
  const nodes = Array.from(document.querySelectorAll(sel));
  return nodes.map(el => {
    const cs = getComputedStyle(el);
    return {
      el,
      display: cs.display,
      visibility: cs.visibility,
      size: el.offsetWidth * el.offsetHeight,
      ariaHidden: el.getAttribute?.('aria-hidden') || null,
      match: (cs.display !== 'none' && cs.visibility !== 'hidden' && el.offsetWidth * el.offsetHeight > 0 && el.getAttribute?.('aria-hidden') !== 'true')
    };
  });
}
function rebuyPopupVisible() {
  try {
    const scan = __scanRebuyPopupCandidates();
    const any = scan.some(x => x.match);
    if (any) {
      const first = scan.find(x => x.match);
      DBG('[Popup] 👀 Visible detected at:', first?.el, {display: first?.display, visibility: first?.visibility, size: first?.size, ariaHidden: first?.ariaHidden});
    } else {
      DBG('[Popup] 👌 None visible (scan size:', scan.length, ')');
    }
    return any;
  } catch { return false; }
}
function isRebuyPopupExpected() {
  const expected = Date.now() < (window.__rebuyPopupExpectedUntil || 0);
  DBG('[Popup] ⏳ Expected window?', expected, 'until=', new Date(window.__rebuyPopupExpectedUntil || 0).toLocaleTimeString());
  return expected;
}

/* ----- RebuyHydrator: run Rebuy.init() once per render (logs) ----- */
(function attachRebuyHydrator() {
  if (window.RebuyHydrator) return;
  const Hydrator = {
    _pending: false,
    _lastInitAt: 0,
    _minInterval: 400,
    markRender(reason='render') { DBG('[RebuyHydrator] 🏷️ markRender:', reason); this.schedule(reason); },
    schedule(reason = 'manual') {
      if (this._pending) { DBG('[RebuyHydrator] ⏸️ already pending; coalesce'); return; }
      this._pending = true;
      Promise.resolve().then(() => requestAnimationFrame(() => this._run(reason)));
    },
    _run(reason) {
      this._pending = false;
      const now = Date.now();
      if (now - this._lastInitAt < this._minInterval) {
        DBG('[RebuyHydrator] ⏱️ throttled; deferring init. Reason:', reason);
        this._pending = true;
        setTimeout(() => this._run(reason + ':coalesce'), this._minInterval);
        return;
      }
      try {
        DBG('[RebuyHydrator] ⚙️ init() reason=', reason);
        window.Rebuy?.init?.();
        this._lastInitAt = Date.now();
      } catch (e) { console.warn('[RebuyHydrator] init() failed:', e); }
    },
  };
  window.RebuyHydrator = Hydrator;
  DBG('[RebuyHydrator] ✅ attached');
})();

/* ----- Cart Drawer ----- */
class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.__openInProgress = false;

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('click', (evt) => {
      const inOverlay = evt.target instanceof Element && evt.target.closest?.('#CartDrawer-Overlay');
      if (inOverlay) {
        evt.preventDefault();
        this.close();
      }
    });
    this.setHeaderCartIconAccessibility();

    this.__stale = false;
    window.addEventListener('cart-drawer:stale', () => { this.__stale = true; DBG('[Cart Drawer] 🟡 Marked stale via event'); });
    document.addEventListener('rebuy:cart.change', () => { this.__stale = true; DBG('[Cart Drawer] 🟡 Marked stale via rebuy:cart.change'); });

    // Diagnostic: who toggles "active" outside open()?
    const mo = new MutationObserver((muts) => {
      for (const m of muts) {
        if (m.type === 'attributes' && m.attributeName === 'class') {
          const isActive = this.classList.contains('active');
          if (isActive && !this.__openInProgress) {
            DBG('[Cart Drawer] 🔎 "active" class added WITHOUT open(); external trigger suspected');
          }
          if (!isActive && this.__openInProgress) {
            DBG('[Cart Drawer] 🔎 "active" removed WHILE open in progress');
          }
        }
      }
    });
    mo.observe(this, { attributes: true, attributeFilter: ['class'] });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');

    cartLink.addEventListener('click', async (e) => { e.preventDefault(); DBG('[Cart Drawer] 🖱️ Header icon click → open()'); await this.open(cartLink); });
    cartLink.addEventListener('keydown', async (e) => {
      if (e.code.toUpperCase() === 'SPACE') { e.preventDefault(); DBG('[Cart Drawer] ⌨️ Header icon SPACE → open()'); await this.open(cartLink); }
    });
  }

  async fetchAndRenderCart({ autoOpen = false } = {}) {
    DBG('[Cart Drawer] 📥 fetchAndRenderCart(autoOpen=', autoOpen, ')');
    const sections = await fetch('/?sections=cart-drawer,cart-icon-bubble').then(r => r.json());
    this.renderContents(
      { id: Date.now(), sections: { 'cart-drawer': sections['cart-drawer'], 'cart-icon-bubble': sections['cart-icon-bubble'] } },
      { autoOpen }
    );
  }

  async open(triggeredBy) {
    DBG('[Cart Drawer] 🚪 open() called. stale=', this.__stale);

    this.removeAttribute('inert');

    // Diagnostic-only: record popup state (no blocking)
    const visible = rebuyPopupVisible();
    const expected = Date.now() < (window.__rebuyPopupExpectedUntil || 0);
    DBG('[Cart Drawer] 🔬 open() popup visible?', visible, 'expected?', expected, 'expectedUntil=', window.__rebuyPopupExpectedUntil || 0);

    if (this.__stale) {
      try { await this.fetchAndRenderCart({ autoOpen: false }); }
      catch (e) { console.warn('[Cart Drawer] stale refresh failed:', e); }
      this.__stale = false;
    }

    if (triggeredBy) this.setActiveElement(triggeredBy);

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    const inner = this.querySelector('.drawer__inner');
    if (inner) { inner.style.overflowY = 'auto'; inner.style.webkitOverflowScrolling = 'touch'; }

    this.__openInProgress = true;
    DBG('[Cart Drawer] 🎬 adding classes animate/active');
    setTimeout(() => { this.classList.add('animate', 'active'); });

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      try { trapFocus(containerToTrapFocusOn, focusElement); } catch {}
      this.__openInProgress = false;
      DBG('[Cart Drawer] ✅ transitionend → focus trapped');
    }, { once: true });

    document.body.classList.add('overflow-hidden');
    DBG('[Cart Drawer] 🧠 overflow-hidden added to body');
    this.pushCartViewGa4Event();
  }

  close() {
    DBG('[Cart Drawer] ❎ close() called');
    this.setAttribute('inert', '');
    this.classList.remove('active');
    try { removeTrapFocus(this.activeElement); } catch {}
    document.body.classList.remove('overflow-hidden');
  }

  async pushCartViewGa4Event() {
    try {
      const cartData = await fetch('/cart.js').then(res => res.json());
      window.dataLayer?.push?.({
        event: 'view_cart',
        value: (cartData.total_price / 100).toFixed(2),
        items: cartData.items.map((item) => ({
          item_id: item.id, item_name: item.title, quantity: item.quantity, price: (item.price / 100).toFixed(2),
        })),
      });
      DBG('[Cart Drawer] 📊 GA4 view_cart pushed (items=', cartData.items?.length, ')');
    } catch (err) { console.warn('[Cart Drawer] GA4 cart fetch failed:', err); }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');
    if (cartDrawerNote.nextElementSibling?.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }
    cartDrawerNote.addEventListener('click', (event) => {
      const open = event.currentTarget.closest('details')?.hasAttribute('open');
      event.currentTarget.setAttribute('aria-expanded', !open);
    });
    cartDrawerNote.parentElement?.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, options = { autoOpen: true }) {
    DBG('[Cart Drawer] 🧩 renderContents(autoOpen=', options.autoOpen, ') sections=', Object.keys(parsedState.sections || {}));

    const wasEmpty = this.querySelector('.drawer__inner')?.classList.contains('is-empty');
    if (wasEmpty) this.querySelector('.drawer__inner')?.classList.remove('is-empty');

    this.productId = parsedState.id;

    const sections = this.getSectionsToRender();
    sections.forEach((section) => {
      const sectionId = section.id;
      const selector = section.selector || `#${section.id}`;
      const targetEl = document.querySelector(selector);
      if (!targetEl) { console.warn(`[Cart Drawer] ❌ Target not found for ${selector}`); return; }
      const html = parsedState.sections?.[sectionId];
      if (!html) { console.warn(`[Cart Drawer] ❌ HTML missing for section ${sectionId}`); return; }
      const inner = this.getSectionInnerHTML(html, selector);
      if (!inner) { console.warn(`[Cart Drawer] ❌ Selector ${selector} missing in HTML`); return; }
      targetEl.innerHTML = inner;
      DBG('[Cart Drawer] 🔄 injected section →', sectionId, 'into', selector);
    });

    if (parsedState.sections?.['cart-icon-bubble']) replaceCartIconBubbleHTML(parsedState.sections['cart-icon-bubble']);
    queueMicrotask(() => refreshCartBadgeFromCart());

    window.RebuyHydrator?.markRender('renderContents');

    setTimeout(() => {
      if (!options.autoOpen) { DBG('[Cart Drawer] 🚫 auto-open disabled by options'); return; }
      // diagnostic — record state at auto-open time
      const vis = rebuyPopupVisible();
      const exp = Date.now() < (window.__rebuyPopupExpectedUntil || 0);
      DBG('[Cart Drawer] 🧭 auto-open tick → popup visible?', vis, 'expected?', exp);
      this.open();
    }, 10);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const el = doc.querySelector(selector);
    return el?.innerHTML || '';
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) { this.activeElement = element; }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  }
}
customElements.define('cart-drawer-items', CartDrawerItems);

class RebuyRecommendedProductGridObserver extends HTMLElement {
  connectedCallback() {
    if (!this.nextElementSibling) return;

    setTimeout(() => {
      this.nextElementSibling.setAttribute('aria-label', 'You might also like');
    }, 1000);
  }
}
customElements.define('rebuy-recommended-product-grid-observer', RebuyRecommendedProductGridObserver);
